import {
  getFreeTextSearchResults,
  getDefaultClientSettings,
} from '@search/api';
import type { FreeTextSearchResponse, FreeTextSearchEntry } from '@search/api';
import type { ProductGroup } from '@search/api/src/types';
import { i18n } from '../utils/i18n';

import {
  buildIndex,
  frontierToSelection,
  normaliseFrontier,
  partitionSelection,
  selectedChildCount,
  selectionStateOf,
  toDestinationNodes,
  toggleNode,
  type DestinationIndex,
  type DestinationNode,
  type OffTreeSelections,
} from './destination-selection-core';

export interface DestinationSelectionItem {
  id: number;
  name: string;
  isHotel?: boolean;
}

/**
 * Selection state. `frontier` is canonical for destinations that exist in the
 * tree (see destination-selection-core.ts). `offTree` holds everything the tree
 * does not contain — accommodations, and destinations the pruned feed omits —
 * each tagged with its own `isHotel`, because tree membership does NOT imply
 * which namespace a selection belongs to.
 */
interface SelectionState {
  frontier: Set<number>;
  offTree: OffTreeSelections;
  expanded: Set<number>;
}

class DestinationModal {
  private readonly destinations: DestinationNode[];
  private readonly index: DestinationIndex;
  private readonly state: SelectionState;
  private modal: any = null;
  private treeContainer: HTMLElement | null = null;
  private freeTextResults: HTMLElement | null = null;
  private subheaderElement: HTMLElement | null = null;
  private confirmed = false;
  private stateSnapshot: {
    frontier: Set<number>;
    offTree: OffTreeSelections;
  } | null = null;
  private readonly onConfirm?: (selected: DestinationSelectionItem[]) => void;

  constructor(
    destinations: ProductGroup[],
    onConfirm?: (selected: DestinationSelectionItem[]) => void
  ) {
    this.destinations = toDestinationNodes(destinations);
    // Built once; every selection query is a cheap read against it, and it is
    // available before the modal is ever opened so defaults can be applied.
    this.index = buildIndex(this.destinations);
    this.onConfirm = onConfirm;
    this.state = {
      frontier: new Set<number>(),
      offTree: new Map(),
      expanded: new Set<number>(),
    };
  }

  /**
   * Seed the destination selection from bare ids (CMS defaults, query strings).
   * Names are resolved from the tree, unknown ids are dropped, and a configured
   * parent+child pair is normalised to the parent.
   */
  public setSelectedDestinationIds(ids: number[]): void {
    this.state.frontier = normaliseFrontier(this.index, ids);
    if (this.treeContainer) {
      this.renderTree();
    }
    this.updateSelectedTags();
  }

  /** Whether `id` is a destination in this modal's tree. */
  public offers(id: number): boolean {
    return this.index.byId.has(id);
  }

  public open(): void {
    // Save state snapshot so we can restore on cancel/dismiss
    this.confirmed = false;
    this.stateSnapshot = {
      frontier: new Set(this.state.frontier),
      offTree: new Map(this.state.offTree),
    };

    const subheader = this.createSubHeader();
    const footer = this.createFooter();

    this.modal = new (window as any).Modal({
      title: i18n.t('modal.destination.title'),
      content: '<div id="destination-tree"></div>',
      className: 'destination-header-title',
      subHeader: subheader,
      subHeaderPadding: false,
      footer: footer,
      padding: false,
      trackingLabel: 'searchComponent-destination',
      onClose: () => {
        if (!this.confirmed && this.stateSnapshot) {
          // Restore state to what it was before the modal opened
          this.state.frontier = new Set(this.stateSnapshot.frontier);
          this.state.offTree = new Map(this.stateSnapshot.offTree);
        }
        this.stateSnapshot = null;
      },
    }).open();

    if (this.modal) {
      const title = document.querySelector(
        '.destination-header-title .modal-title'
      ) as HTMLElement;
      if (title) {
        title.dataset.testid = 'destination-search-header';
      }
    }

    this.treeContainer = document.getElementById('destination-tree');
    if (this.treeContainer) {
      this.renderTree();
      this.treeContainer.dataset.testid = 'destination-search-header';
      this.treeContainer.addEventListener('destinationSelectionChange', () => {
        this.updateSelectedTags();
      });
    }

    // Update tags to show existing selections when modal reopens
    this.updateSelectedTags();
  }

  public close(): void {
    this.modal?.close();
  }

  public getSelection(): DestinationSelectionItem[] {
    const inTree: DestinationSelectionItem[] = frontierToSelection(
      this.index,
      this.state.frontier
    ).map(item => ({ ...item, isHotel: false }));

    const offTree: DestinationSelectionItem[] = Array.from(
      this.state.offTree,
      ([id, { name, isHotel }]) => ({ id, name, isHotel })
    );

    return [...inTree, ...offTree];
  }

  /**
   * The single ingest point for externally supplied selections — CMS defaults,
   * query strings, chip removal. Destination ids are normalised so the frontier
   * invariant holds no matter what the caller passes (a Contentful config listing
   * both a country and one of its regions is normalised to the country).
   */
  public setSelection(items: DestinationSelectionItem[]): void {
    const { frontier, offTree } = partitionSelection(this.index, items);
    this.state.frontier = frontier;
    this.state.offTree = offTree;

    if (this.treeContainer) {
      this.renderTree();
    }
    this.updateSelectedTags();
  }

  private createSubHeader(): HTMLElement {
    const container = document.createElement('div');
    container.className =
      'flex flex-col gap-3 bg-secondary pt-4 px-4 border-b border-component';

    const searchInput = this.createSearchInput();
    container.appendChild(searchInput);

    const tagsContainer = this.createTagsContainer();
    container.appendChild(tagsContainer);

    this.subheaderElement = container;
    return container;
  }

  private createSearchInput(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'relative';

    const input = document.createElement('input');
    input.className = 'form-input p-4 tt-label-sm';
    input.type = 'text';
    input.name = i18n.t('modal.destination.searchPlaceholder');
    input.placeholder = i18n.t('modal.destination.searchPlaceholder');
    input.ariaLabel = i18n.t('modal.destination.searchPlaceholder');
    input.dataset.testid = 'destination-search-input';

    let timeoutId: number;

    input.addEventListener('input', e => {
      const query = (e.target as HTMLInputElement).value;
      clearTimeout(timeoutId);

      if (query.length >= 3) {
        timeoutId = window.setTimeout(() => {
          this.searchDestinations(query).then(results => {
            if (
              results &&
              [
                ...results.accommodations,
                ...results.cities,
                ...results.countries,
                ...results.regions,
              ].length > 0
            ) {
              this.showSearchResults(results, input);
            }
          });
        }, 300);
      } else {
        this.removeSearchResults();
      }
    });

    container.appendChild(input);
    return container;
  }

  private createTagsContainer(): HTMLElement {
    const container = document.createElement('div');
    container.dataset.selectedTags = 'true';
    container.className = 'flex gap-3 py-1 px-1 mx-1 overflow-x-auto';

    const tag = document.createElement('div');
    tag.className =
      'p-3 rounded-lg border border-functional bg-functional-tint flex gap-2 items-center shrink-0';

    const pinSvg = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg'
    );
    pinSvg.classList.add('icon', 'icon-xs');

    const pinSvgUse = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'use'
    );
    pinSvgUse.setAttribute('href', '#location-pin');

    pinSvg.appendChild(pinSvgUse);
    tag.appendChild(pinSvg);

    const tagText = document.createElement('span');
    tagText.className = 'tt-label-sm';
    tagText.textContent = i18n.t('modal.search.anyDestination');

    tag.appendChild(tagText);
    container.appendChild(tag);

    return container;
  }

  private updateSelectedTags(): void {
    const container = this.subheaderElement?.querySelector(
      '[data-selected-tags]'
    );
    if (!container) return;

    const selected = this.getSelection();
    container.innerHTML = '';

    if (selected.length === 0) {
      const tag = document.createElement('div');
      tag.className =
        'p-3 rounded-lg border border-functional bg-functional-tint flex gap-2 items-center shrink-0';

      const pinSvg = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      pinSvg.classList.add('icon', 'icon-xs');

      const pinSvgUse = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'use'
      );
      pinSvgUse.setAttribute('href', '#location-pin');

      pinSvg.appendChild(pinSvgUse);
      tag.appendChild(pinSvg);

      const tagText = document.createElement('span');
      tagText.className = 'tt-label-sm';
      tagText.textContent = i18n.t('search.anyDestination');
      tag.appendChild(tagText);
      container.appendChild(tag);
    } else {
      selected.forEach(item => {
        const tag = document.createElement('button');
        tag.className =
          'p-3 rounded-lg border border-functional bg-functional-tint flex gap-2 items-center shrink-0';
        tag.dataset.testid = `destination-remove-tag-cta-${item.id}`;

        const pinSvg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );
        pinSvg.classList.add('icon', 'icon-xs');

        const pinSvgUse = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'use'
        );
        pinSvgUse.setAttribute('href', '#location-pin');

        pinSvg.appendChild(pinSvgUse);
        tag.appendChild(pinSvg);

        const tagText = document.createElement('span');
        tagText.className = 'tt-label-sm';
        tagText.textContent = item.name;

        const crossSvg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );
        crossSvg.classList.add('icon', 'icon-xs');

        const crossSvgUse = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'use'
        );
        crossSvgUse.setAttribute('href', '#cross');

        crossSvg.appendChild(crossSvgUse);
        tag.appendChild(crossSvg);

        tag.appendChild(tagText);
        tag.appendChild(crossSvg);

        tag.addEventListener('click', () => {
          const current = this.getSelection();
          this.setSelection(current.filter(i => i.id !== item.id));
        });

        container.appendChild(tag);
      });
    }
  }

  private async searchDestinations(
    query: string
  ): Promise<FreeTextSearchResponse | undefined> {
    const results = await getFreeTextSearchResults(
      query.trim(),
      getDefaultClientSettings(i18n.getLocale())
    );

    return results;
  }

  private createSearchHeading(name: string, icon: string): HTMLHeadingElement {
    const heading = document.createElement('h4');
    heading.classList.add(
      'p-4',
      'tt-heading-md',
      'flex',
      'items-center',
      'gap-2'
    );
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('icon');
    svg.ariaHidden = 'true';

    const svgUse = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'use'
    );
    svgUse.setAttribute('href', icon);

    svg.appendChild(svgUse);
    heading.appendChild(svg);

    heading.innerHTML += name;

    return heading;
  }

  private createSearchItems(
    items: FreeTextSearchEntry[],
    inputElement: HTMLInputElement,
    level: string
  ): HTMLButtonElement[] {
    const elements: HTMLButtonElement[] = [];

    items.forEach((pg: FreeTextSearchEntry) => {
      const btn = document.createElement('button');
      btn.className = 'p-4 block w-full text-left';
      btn.dataset.testid = `search-${level}-${pg.id}`;
      btn.textContent = pg.value;
      btn.addEventListener('click', () => {
        this.selectFromSearchResults(Number(pg.id), pg.value, false);
        this.removeSearchResults();
        inputElement.value = '';
      });

      elements.push(btn);
    });

    return elements;
  }

  private selectFromSearchResults(
    id: number,
    name: string,
    isHotel: boolean
  ): void {
    // Re-partition the whole selection rather than mutating one namespace in
    // place. That keeps the interactive path on exactly the same (tested) rules
    // as every other writer: isHotel decides the namespace, an off-tree
    // destination stays a destination, and an id can never end up in both.
    const others = this.getSelection().filter(item => item.id !== id);
    this.setSelection([...others, { id, name, isHotel }]);
  }

  private showSearchResults(
    results: FreeTextSearchResponse,
    inputElement: HTMLInputElement
  ): void {
    this.removeSearchResults();

    const container = document.createElement('div');
    container.className =
      'absolute mt-2 left-0 right-0 max-h-96 overflow-y-scroll border rounded z-10 bg-secondary';
    container.dataset.searchResults = 'true';
    container.dataset.testid = 'destination-search-modal';

    const closeBtn = document.createElement('button');
    closeBtn.className =
      'absolute top-0 right-4 bg-secondary p-4 pr-0 underline text-link tt-label-md';
    closeBtn.textContent = 'Close';
    closeBtn.dataset.testid = 'destination-search-modal-close';
    closeBtn.addEventListener('click', () => this.removeSearchResults());
    container.appendChild(closeBtn);

    //Destinations
    if (results.countries.length > 0) {
      const destinationsHeading = this.createSearchHeading(
        'Destinations',
        '#destinations'
      );
      container.appendChild(destinationsHeading);
      container.append(
        ...this.createSearchItems(results.countries, inputElement, 'country')
      );
    }

    //Regions
    if (results.regions.length > 0) {
      const regionsHeading = this.createSearchHeading(
        'Regions',
        '#location-map'
      );
      container.appendChild(regionsHeading);
      container.append(
        ...this.createSearchItems(results.regions, inputElement, 'region')
      );
    }

    //Resorts
    if (results.cities.length > 0) {
      const resortsHeading = this.createSearchHeading(
        'Resorts',
        '#location-map'
      );
      container.appendChild(resortsHeading);
      container.append(
        ...this.createSearchItems(results.cities, inputElement, 'resort')
      );
    }

    //Accommodations
    if (results.accommodations.length > 0) {
      const accommodationHeading = this.createSearchHeading('Hotels', '#hotel');
      container.appendChild(accommodationHeading);

      results.accommodations.forEach(acc => {
        const btn = document.createElement('button');
        btn.className = 'p-4 block w-full text-left';
        btn.dataset.testid = `search-hotel-${acc.id}`;
        btn.textContent = acc.value;
        btn.addEventListener('click', () => {
          this.selectFromSearchResults(Number(acc.id), acc.value, true);
          this.removeSearchResults();
          inputElement.value = '';
        });
        container.appendChild(btn);
      });
    }

    this.freeTextResults = container;
    inputElement.parentElement?.appendChild(container);
  }

  private removeSearchResults(): void {
    this.freeTextResults?.remove();
    this.freeTextResults = null;
  }

  private createFooter(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'flex gap-3 justify-around';

    const clearBtn = document.createElement('button');
    clearBtn.className = 'btn btn-secondary btn-lg grow';
    clearBtn.textContent = i18n.t('modal.common.clearAll');
    clearBtn.dataset.action = 'clear';
    clearBtn.dataset.testid = 'search-modal-clearall';
    clearBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.setSelection([]);
      this.onConfirm?.(this.getSelection());
    });

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn btn-primary btn-lg grow';
    confirmBtn.textContent = i18n.t('modal.common.confirm');
    confirmBtn.dataset.action = 'confirm';
    confirmBtn.dataset.testid = 'search-modal-confirm';
    confirmBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.onConfirm?.(this.getSelection());
      this.close();
    });

    container.appendChild(clearBtn);
    container.appendChild(confirmBtn);
    return container;
  }

  private renderTree(): void {
    if (!this.treeContainer) return;

    const activeElement = document.activeElement as HTMLElement;
    const focusedDestinationId = activeElement
      ?.closest('[data-destination-id]')
      ?.getAttribute('data-destination-id');
    const focusedElementType = activeElement?.tagName.toLowerCase();
    const shouldRestoreFocus = this.treeContainer.contains(activeElement);

    this.treeContainer.setAttribute('role', 'tree');
    this.treeContainer.innerHTML = '';

    this.destinations.forEach(dest => {
      const element = this.createTreeItem(dest, 1);
      this.treeContainer!.appendChild(element);
    });

    if (shouldRestoreFocus && focusedDestinationId) {
      const newElement = this.treeContainer.querySelector(
        `[data-destination-id="${focusedDestinationId}"] ${focusedElementType}`
      ) as HTMLElement;
      newElement?.focus();
    }
  }

  private createTreeItem(
    destination: DestinationNode,
    level: number
  ): HTMLElement {
    const hasChildren = (destination.children?.length ?? 0) > 0;
    const isExpanded = this.state.expanded.has(destination.id);
    // Derived from the frontier — never read from a stored copy.
    const state = selectionStateOf(
      this.index,
      this.state.frontier,
      destination.id
    );
    const isChecked = state === 'checked';
    const isMixed = state === 'mixed';

    const treeItem = document.createElement('div');
    treeItem.setAttribute('role', 'treeitem');
    treeItem.dataset.destinationId = destination.id.toString();
    treeItem.dataset.testid = `destination-checkbox-${destination.id}`;
    const content = document.createElement('div');
    content.className =
      'flex items-center form-label w-full tt-body-md py-2 pr-2 pl-4 border-b border-component';

    if (isChecked) {
      content.classList.add('bg-functional-tint');
    }

    content.style.paddingLeft = `${level}rem`;

    if (!hasChildren) {
      content.classList.add('py-4');
      content.classList.remove('py-2');
    }

    const checkboxWrapper = document.createElement('div');
    checkboxWrapper.className = 'flex flex-1 items-center gap-4';

    const checkboxContainer = document.createElement('div');
    checkboxContainer.classList.add('flex', 'items-center', 'relative');

    const checkbox = document.createElement('input');
    checkbox.className = 'form-checkbox';
    checkbox.type = 'checkbox';
    checkbox.checked = isChecked;
    checkbox.indeterminate = isMixed;

    if (isChecked || isMixed) {
      checkbox.classList.add('border-primary', 'bg-primary');
    }

    const selectedCount = isMixed
      ? selectedChildCount(this.index, this.state.frontier, destination.id)
      : 0;

    // `change` is the single selection path, so keyboard activation (space) works
    // as well as mouse — mirroring AirportModal. The checkbox is re-rendered from
    // the frontier, so it is never flipped by hand here; doing that is what let
    // the visual and canonical state diverge.
    checkbox.addEventListener('change', () => {
      this.toggleDestination(destination.id);
    });

    content.addEventListener('click', e => {
      // Clicks on the checkbox itself are already handled by `change`; anything
      // else on the row is forwarded to it so there is only one code path.
      if (e.target === checkbox) return;
      checkbox.click();
    });

    checkboxContainer.appendChild(checkbox);

    if (isMixed && selectedCount > 0) {
      const countBadge = document.createElement('span');
      countBadge.className = 'text-secondary text-xs font-bold';
      countBadge.style.position = 'absolute';
      countBadge.style.left = '50%';
      countBadge.style.transform = 'translate(-50%, 0)';
      countBadge.style.pointerEvents = 'none';
      countBadge.textContent = selectedCount.toString();
      checkboxContainer.appendChild(countBadge);
    }

    const label = document.createElement('label');
    label.textContent = destination.name;
    label.style.flex = '1';
    label.id = `label-search-modal-${destination.id}`;

    checkbox.setAttribute('aria-labelledby', label.id);

    checkboxWrapper.appendChild(checkboxContainer);
    checkboxWrapper.appendChild(label);
    content.appendChild(checkboxWrapper);

    if (hasChildren) {
      const expandBtn = document.createElement('button');

      expandBtn.className = 'p-2';
      expandBtn.dataset.testid = `destination-expand-${destination.id}`;
      expandBtn.addEventListener('click', e => {
        e.stopPropagation();
        this.toggleExpand(destination.id);
      });

      const arrowSvg = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      arrowSvg.classList.add('icon', 'icon-sm', 'icon-dark');

      const arrowSvgUse = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'use'
      );
      arrowSvgUse.setAttribute(
        'href',
        isExpanded ? '#chevron-up' : '#chevron-down'
      );

      arrowSvg.appendChild(arrowSvgUse);
      expandBtn.appendChild(arrowSvg);
      content.appendChild(expandBtn);
    }

    treeItem.appendChild(content);

    if (hasChildren) {
      const childrenContainer = document.createElement('div');
      childrenContainer.style.display = isExpanded ? 'block' : 'none';
      destination.children!.forEach(child => {
        childrenContainer.appendChild(this.createTreeItem(child, level + 1));
      });
      treeItem.appendChild(childrenContainer);
    }

    return treeItem;
  }

  private toggleExpand(id: number): void {
    this.state.expanded.has(id)
      ? this.state.expanded.delete(id)
      : this.state.expanded.add(id);
    this.renderTree();
  }

  private toggleDestination(id: number): void {
    this.state.frontier = toggleNode(this.index, this.state.frontier, id);
    this.renderTree();
    this.notifyChange();
  }

  private notifyChange(): void {
    const event = new CustomEvent('destinationSelectionChange', {
      detail: { selectedDestinations: this.getSelection() },
    });
    this.treeContainer?.dispatchEvent(event);
  }
}

export default DestinationModal;
